import React, { useEffect, useState } from 'react';
import { camelizeKeys } from '../../../../../utils/camelizeKeys';
import { FileText, X } from 'lucide-react';
import Button from '../../../../../components/ui/buttons/Button';
import MultipleFileInput from '../../../../../components/ui/buttons/MultipleFileInput';
import { useToast } from '../../../../../components/ui/toast/ToastContext';
import { fetchWithCsrf } from '../../../../../utils/fetchWithCsrf';
import { useConfirm } from '../../../../../components/ui/confirm/ConfirmContext';

const DocumentsTab = ({ clientId, scope, canWrite }) => {
  const [data, setData] = useState({
    documents: [],
  });

  const [filesToUpload, setFilesToUpload] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const { addToast } = useToast();
  const confirm = useConfirm();

  useEffect(() => {
    fetchData();
  }, [clientId, scope]);

  const fetchData = () => {
    fetch(`/app/planner/client/${clientId}/dashboard/documents?scope=${scope}`)
      .then((res) => res.json())
      .then((data) => {
        const camelData = camelizeKeys(data);

        setData(camelData);
      });
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (filesToUpload.length === 0) {
      addToast({ type: 'error', message: 'Selecione pelo menos um arquivo.' });
      return;
    }

    const formData = new FormData();
    filesToUpload.forEach((file) => formData.append('files[]', file));
    formData.append('scope', scope);

    setIsSaving(true);
    try {
      const res = await fetchWithCsrf(`/app/planner/client/${clientId}/dashboard/documents`, {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.message || 'Erro ao enviar documentos');
      }

      addToast({ type: 'success', message: 'Documentos enviados com sucesso!' });
      setFilesToUpload([]);
      fetchData();
    } catch (err) {
      addToast({ type: 'error', message: err.message });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (documentId) => {
    const ok = await confirm({
      title: 'CONFIRMAR EXCLUSÃO?',
      description: 'Deseja realmente excluir este documento?',
      confirmText: 'SIM, EXCLUIR',
    });
    if (!ok) return;

    try {
      const res = await fetchWithCsrf(
        `/app/planner/client/${clientId}/dashboard/documents/${documentId}?scope=${scope}`,
        { method: 'DELETE' }
      );
      if (!res.ok) throw new Error('Erro ao excluir documento');

      addToast({ type: 'success', message: 'Documento excluído com sucesso!' });
      setData((prev) => ({ ...prev, documents: prev.documents.filter((doc) => doc.id !== documentId) }));
    } catch (err) {
      addToast({ type: 'error', message: err.message });
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-5 gap-5">
        {/* col 1 */}
        <div className={`flex flex-col ${canWrite ? 'col-span-3' : 'col-span-5'} gap-5 bg-white rounded-custom20 px-6 py-4`}>
          <h3 className="font-semibold text-xl text-blueDarker">Documentos enviados</h3>
          {console.log('DocumentsTab data:', data)}
          <div className="flex flex-col gap-4">
            {data.documents.map((doc) => (
              <div key={doc.id} className="flex items-center justify-between rounded-xl bg-blueLight px-5 py-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="rounded-full bg-white p-2">
                    <FileText size={24} className="text-tealLight" strokeWidth={1.5} />
                  </span>
                  <span className="text-blueDarker font-semibold text-base truncate" title={doc.title}>
                    {doc.title}
                  </span>
                </div>
                <div className="flex gap-20 items-center">
                  <span className="text-blueSteel text-sm">
                    Anexado em{' '}
                    {new Date(doc.createdAt).toLocaleDateString('pt-BR', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                    })}
                  </span>
                  <div className="flex gap-5">
                    <button onClick={() => window.open(doc.url, '_blank')}>
                      <span className="text-tealBase font-medium text-base underline">Ver documento</span>
                    </button>
                    {canWrite && (
                      <button onClick={() => handleDelete(doc.id)}>
                        <span className="text-redSoft font-medium text-base underline">Excluir</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {canWrite && (
        <div className="rounded-custom20 col-span-2 bg-white px-6 py-4 flex flex-col gap-5">
          <h3 className="font-semibold text-xl text-blueDarker text-center">Enviar um novo documento</h3>
          <div
            className="flex flex-col items-center justify-center border-dashed border border-tealSteel rounded-lg h-72 text-blueSteelDark gap-5"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const droppedFiles = Array.from(e.dataTransfer.files);
              setFilesToUpload((prev) => [...prev, ...droppedFiles]);
            }}
          >
            <span className="rounded-full bg-blueExtraPale p-8">
              <FileText size={60} strokeWidth={1.5} />
            </span>
            <span className="text-lg font-medium">Arraste e solte o arquivo ou clique para selecionar</span>
            <div className="flex flex-col items-center">
              <span className="text-sm">
                Arquivos suportados: <span>xls, xlsx, pdf, jpg, csv, doc, docx, zip.</span>
              </span>
              <span className="text-sm">
                Tamanho máximo permitido: <span>20 MB</span>
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            {filesToUpload.length === 0 ? (
              <span></span>
            ) : (
              filesToUpload.map((file, index) => (
                <div key={index} className="flex gap-2 items-center">
                  <FileText size={16} className="text-tealLight" />
                  <span className="text-blueSteel text-sm italic">{file.name}</span>
                  <button
                    onClick={() => {
                      setFilesToUpload((prev) => prev.filter((_, i) => i !== index));
                    }}
                  >
                    <X size={16} className="text-redSoft" />
                  </button>
                </div>
              ))
            )}
          </div>
          <MultipleFileInput
            onFileSelect={(selectedFiles) => {
              setFilesToUpload((prev) => [...prev, ...selectedFiles]);
            }}
          />
          <div className="flex justify-center">
            <Button onClick={handleUpload} disabled={isSaving || filesToUpload.length === 0}>
              ENVIAR DOCUMENTOS
            </Button>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default DocumentsTab;
